import React from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ChevronRight } from "lucide-react";

const HeroSection = () => {
  const { scrollYProgress } = useScroll();
  const yScroll = useTransform(scrollYProgress, [0, 1], [0, -100]);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const smoothMouseX = useSpring(mouseX, {
    stiffness: 60,
    damping: 20,
  });

  const smoothMouseY = useSpring(mouseY, {
    stiffness: 60,
    damping: 20,
  });

  const rotateX = useTransform(smoothMouseY, [-30, 30], [6, -6]);
  const rotateY = useTransform(smoothMouseX, [-30, 30], [-6, 6]);

  const handleMouseMove = (e) => {
    if (window.innerWidth < 768) return;

    const x = (e.clientX / window.innerWidth - 0.5) * 40;
    const y = (e.clientY / window.innerHeight - 0.5) * 40;

    mouseX.set(x);
    mouseY.set(y);
  };

  return (
    <section
      onMouseMove={handleMouseMove}
      className="relative flex min-h-[auto] w-full flex-col justify-center overflow-hidden bg-[#020B1C] py-12 font-sans md:min-h-[700px] md:py-0 lg:h-[700px] lg:pt-[50px]"
    >
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(86.84deg, #000000 20%, #01386A 79%)",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-[1340px] flex-col items-center gap-8 px-5 sm:px-8 pt-[75px] md:flex-row-reverse md:justify-between md:gap-6 md:pt-0 lg:px-20">
        <div
          className="order-2 flex w-full items-center justify-end md:order-1 md:w-1/2"
          style={{ perspective: "1200px" }}
        >
          <motion.div
            style={{
              y: yScroll,
              x: smoothMouseX,
              rotateX,
              rotateY,
            }}
            className="relative z-10 flex h-[320px] w-full sm:h-[400px] md:h-[480px] lg:h-[540px] items-center justify-center overflow-hidden"
          >
            <div
              className="relative h-full w-full overflow-hidden shadow-2xl"
              style={{
                clipPath: "polygon(0% 100%, 100% 0%, 100% 70%)",
              }}
            >
              <video
                src="/assets/moke.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="h-full w-full object-cover object-center"
              />
            </div>
          </motion.div>
        </div>

        <div className="order-1 flex w-full flex-col justify-center md:order-2 md:w-1/2">
          <div className="w-full max-w-[700px] text-left">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="font-graphik text-[30px] font-medium leading-[1.12] tracking-tight text-white sm:text-[42px] md:text-[38px] lg:text-[48px]"
            >
              ServiceNow. Databricks. Digital engineering.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-5 w-full max-w-[550px] text-[14px] font-medium leading-[1.65] text-white/80 sm:text-[15px] lg:mt-6 lg:text-[16px]"
            >
              Mobikode brings deep consulting expertise across all three to turn your data, workflows and AI into measurable results.
            </motion.p>

            <motion.a
              href="/what-we-do"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="group mt-7 inline-flex w-fit items-center gap-3 border-b border-white/30 pb-1 font-graphik text-[14px] font-medium text-white transition-all duration-300 hover:border-[#ED5800] hover:text-[#ED5800] sm:mt-8 lg:mt-9 lg:text-[16px]"
            >
              See What We Do
              <ChevronRight
                size={22}
                className="text-[#ED5800] transition-transform duration-300 group-hover:translate-x-2"
              />
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;